// Hook: fetch material movements for a WorkOrderV2 and aggregate them into
// a per-component tally (reserved / issued / produced / scrapped).
//
// Movement-type sign conventions (matches backend tally service):
//   reservation         +reserved
//   reservation_release -reserved
//   issue               +issued
//   receipt             +produced
//   scrap               +scrapped
//   adjustment          signed; net flows into 'adjusted'

import { useCallback, useEffect, useRef, useState } from 'react';
import { v2 } from '../../../../services/api';

function toNumber(v) {
  if (v == null) return 0;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
}

function absNumber(v) {
  return Math.abs(toNumber(v));
}

export function aggregateTally(movements) {
  const byItem = new Map();
  if (!Array.isArray(movements)) return [];

  for (const m of movements) {
    if (!m) continue;
    const itemId = m.item;
    if (itemId == null) continue;
    if (!byItem.has(itemId)) {
      byItem.set(itemId, {
        item_id: itemId,
        item_code: m.item_code || '',
        reserved: 0,
        issued: 0,
        produced: 0,
        scrapped: 0,
        adjusted: 0,
      });
    }
    const row = byItem.get(itemId);
    const qty = toNumber(m.quantity);
    switch (m.movement_type) {
      case 'reservation':
        row.reserved += absNumber(qty);
        break;
      case 'reservation_release':
        row.reserved -= absNumber(qty);
        break;
      case 'issue':
        row.issued += absNumber(qty);
        break;
      case 'receipt':
        row.produced += absNumber(qty);
        break;
      case 'scrap':
        row.scrapped += absNumber(qty);
        break;
      case 'adjustment':
        row.adjusted += qty; // signed
        break;
      default:
        break;
    }
  }
  return Array.from(byItem.values()).sort((a, b) =>
    (a.item_code || '').localeCompare(b.item_code || '')
  );
}

export function useTallyV2(workOrderId) {
  const [movements, setMovements] = useState([]);
  const [tally, setTally] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchMovements = useCallback(async () => {
    if (workOrderId == null) {
      setMovements([]);
      setTally([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await v2.materialMovements.list({ work_order: workOrderId });
      const data = res.data?.results || res.data || [];
      if (mountedRef.current) {
        setMovements(data);
        setTally(aggregateTally(data));
      }
    } catch (err) {
      console.error('useTallyV2: failed', err);
      if (mountedRef.current) setError(err);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [workOrderId]);

  useEffect(() => {
    fetchMovements();
  }, [fetchMovements]);

  return { movements, tally, loading, error, refresh: fetchMovements };
}

export default useTallyV2;
